import { useState } from "react";
import { Plus, Trash, CheckCircle, Circle, Eye, EyeSlash, X } from "@phosphor-icons/react";
import api from "@/lib/api";

/**
 * Sub-tasks checklist for the task detail drawer.
 * Stores as an array on task.subtasks: [{id, text, done, done_by?, done_at?}].
 */
export function SubtasksSection({ task, currentUserId, onPatch, onChanged }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const subs = Array.isArray(task?.subtasks) ? task.subtasks : [];
  const done = subs.filter(s => s.done).length;

  const genId = () => `st_${Math.random().toString(36).slice(2, 10)}`;

  const commit = async (next) => {
    setBusy(true);
    try {
      await onPatch({ subtasks: next });
      onChanged && onChanged();
    } finally { setBusy(false); }
  };

  const add = async () => {
    const t = text.trim();
    if (!t) return;
    setText("");
    await commit([...subs, { id: genId(), text: t, done: false }]);
  };

  const toggle = async (idx) => {
    const cur = subs[idx];
    const next = subs.map((s, i) => i === idx
      ? { ...s, done: !s.done, done_by: (!s.done ? currentUserId : null), done_at: (!s.done ? new Date().toISOString() : null) }
      : s);
    await commit(next);
  };

  const remove = async (idx) => {
    await commit(subs.filter((_, i) => i !== idx));
  };

  const editText = async (idx, newText) => {
    if (!newText.trim()) return remove(idx);
    if (newText === subs[idx].text) return;
    const next = subs.map((s, i) => i === idx ? { ...s, text: newText } : s);
    await commit(next);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="eyebrow">Sub-tasks {subs.length > 0 && `(${done}/${subs.length})`}</div>
        {subs.length > 0 && (
          <div className="w-32 h-1.5 bg-muted overflow-hidden">
            <div className="h-full bg-[#002FA7]" style={{ width: `${(done / subs.length) * 100}%` }} />
          </div>
        )}
      </div>
      <ul className="space-y-1">
        {subs.map((s, i) => (
          <li key={s.id} className="flex items-center gap-2 group" data-testid={`subtask-${s.id}`}>
            <button onClick={() => toggle(i)} disabled={busy} className="text-muted-foreground hover:text-[#002FA7]" data-testid={`subtask-toggle-${s.id}`}>
              {s.done ? <CheckCircle size={16} weight="fill" className="text-[#059669]" /> : <Circle size={16} />}
            </button>
            <input
              defaultValue={s.text}
              onBlur={(e) => editText(i, e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
              className={`flex-1 bg-transparent border-b border-transparent hover:border-border focus:border-[#002FA7] focus:outline-none text-sm py-0.5 ${s.done ? "line-through text-muted-foreground" : ""}`}
            />
            <button onClick={() => remove(i)} className="p-1 opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-red-600">
              <Trash size={12} />
            </button>
          </li>
        ))}
      </ul>
      <div className="flex gap-2 mt-2">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="Add sub-task…"
          className="flex-1 border border-border px-3 py-1.5 text-sm"
          data-testid="subtask-new-input"
        />
        <button onClick={add} disabled={busy || !text.trim()} className="btn-primary text-xs px-3 py-1.5 flex items-center gap-1" data-testid="subtask-new-add">
          <Plus size={12} weight="bold" /> Add
        </button>
      </div>
    </div>
  );
}

/**
 * Watchers list + picker for the task detail drawer.
 */
export function WatchersSection({ task, users, currentUserId, onPatch }) {
  const [picking, setPicking] = useState(false);
  const watchers = Array.isArray(task?.watchers) ? task.watchers : [];
  const iAmWatching = watchers.includes(currentUserId);
  const userMap = Object.fromEntries((users || []).map(u => [u.id, u]));
  const setWatchers = async (next) => {
    await onPatch({ watchers: next });
  };

  const toggleSelf = () => setWatchers(iAmWatching ? watchers.filter(x => x !== currentUserId) : [...watchers, currentUserId]);
  const remove = (uid) => setWatchers(watchers.filter(x => x !== uid));
  const addUser = (uid) => { if (!watchers.includes(uid)) setWatchers([...watchers, uid]); };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="eyebrow">Watchers {watchers.length > 0 && `(${watchers.length})`}</div>
        <div className="flex gap-2">
          <button onClick={toggleSelf} className="text-[10px] font-mono-mini uppercase border border-border px-2 py-0.5 hover:bg-muted flex items-center gap-1" data-testid="watch-toggle">
            {iAmWatching ? <><EyeSlash size={12} /> Unwatch</> : <><Eye size={12} /> Watch</>}
          </button>
          <button onClick={() => setPicking(p => !p)} className="text-[10px] font-mono-mini uppercase border border-border px-2 py-0.5 hover:bg-muted flex items-center gap-1">
            <Plus size={12} /> Add
          </button>
        </div>
      </div>
      {watchers.length === 0 ? (
        <div className="text-xs text-muted-foreground">No watchers yet. Watchers get notified on status / priority / assignee / due-date changes.</div>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {watchers.map(uid => {
            const u = userMap[uid];
            return (
              <span key={uid} className="inline-flex items-center gap-1 border border-border bg-muted px-2 py-0.5 text-xs" data-testid={`watcher-${uid}`}>
                {u?.name || uid.slice(0, 6)}
                <button onClick={() => remove(uid)} className="text-muted-foreground hover:text-red-600"><X size={10} weight="bold" /></button>
              </span>
            );
          })}
        </div>
      )}
      {picking && (
        <div className="mt-2 border border-border p-2 max-h-40 overflow-y-auto">
          {(users || []).filter(u => !watchers.includes(u.id)).map(u => (
            <button key={u.id} onClick={() => { addUser(u.id); setPicking(false); }} className="block w-full text-left text-sm px-2 py-1 hover:bg-muted" data-testid={`watch-add-${u.id}`}>
              {u.name} <span className="text-muted-foreground text-xs">({u.role})</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
